import { useEffect, useState, useCallback } from "react";
import AiUsesTable from "../tables/AiUsesTable";
import { listAiUses, getAiUsageSummary } from "../api/aiUsage";
import { ErrorBoundary } from "react-error-boundary";

function ErrorFallback({ error }) {
  return (
    <div className="p-4 bg-red-50 border border-red-400 rounded">
      <h2 className="text-lg font-bold text-red-800">Something went wrong:</h2>
      <pre className="text-red-600">{error.message}</pre>
    </div>
  );
}

export default function AiUsesList() {
  const [records, setRecords] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedDate, setSelectedDate] = useState(""); // State for date filter
  const [searchQuery, setSearchQuery] = useState(""); // State for search query
  const [pagination, setPagination] = useState({
    total: 0,
    limit: 10,
    offset: 0,
  });
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [dailyTotalTokens, setDailyTotalTokens] = useState(0);
  const [maxPerMinuteTokens, setMaxPerMinuteTokens] = useState(0);

  const fetchSummary = useCallback(async (dateValue = "") => {
    try {
      const data = await getAiUsageSummary(dateValue);
      setDailyTotalTokens(data.daily_total_tokens || 0);
      setMaxPerMinuteTokens(data.max_per_minute_tokens || 0);
    } catch (err) {
      console.error("Error fetching AI usage summary:", err);
    }
  }, []);

  const refresh = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await listAiUses(
        rowsPerPage,
        pagination.offset,
        selectedDate,
      );
      setRecords(data.records || []);
      setPagination({
        total: data.total || 0,
        limit: rowsPerPage,
        offset: data.offset || 0,
      });
      // Also fetch summary after records refreshed
      fetchSummary(selectedDate);
    } catch (err) {
      console.error("Error fetching AI usage records:", err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [rowsPerPage, pagination.offset, selectedDate, fetchSummary]);

  // Handle date filter change
  const handleDateChange = (e) => {
    const newDate = e.target.value;
    setSelectedDate(newDate);
    // Reset pagination when changing filter
    setPagination((prev) => ({ ...prev, offset: 0 }));
  };

  // Handle search query change
  const handleSearchChange = (e) => {
    const query = e.target.value;
    setSearchQuery(query);
    // Reset pagination when searching
    setPagination((prev) => ({ ...prev, offset: 0 }));
  };

  // Clear search
  const clearSearch = () => {
    setSearchQuery("");
    setPagination((prev) => ({ ...prev, offset: 0 }));
  };

  // Filter records based on search query
  const filteredRecords = records.filter((record) => {
    const query = searchQuery.toLowerCase();

    // Account information
    const accountId = (record.account_id || "").toString().toLowerCase();
    const businessName = (record.account?.business_name || "").toLowerCase();
    const email = (record.account?.email || "").toLowerCase();

    // Driver information
    const firstName = (record.driver?.first_name || "").toLowerCase();
    const lastName = (record.driver?.last_name || "").toLowerCase();
    const fullName = `${firstName} ${lastName}`.toLowerCase().trim();
    const licenseNumber = (record.driver?.license_number || "").toLowerCase();

    // Search across all fields
    return (
      accountId.includes(query) ||
      businessName.includes(query) ||
      email.includes(query) ||
      firstName.includes(query) ||
      lastName.includes(query) ||
      fullName.includes(query) ||
      licenseNumber.includes(query)
    );
  });

  // Handle rows per page change
  const handleRowsPerPageChange = (e) => {
    const newLimit =
      e.target.value === "all" ? pagination.total : parseInt(e.target.value);
    setRowsPerPage(newLimit);
    // Reset pagination when changing rows per page
    setPagination((prev) => ({ ...prev, offset: 0, limit: newLimit }));
  };

  // Initial load and refetch when dependencies change (including date)
  useEffect(() => {
    refresh();
  }, [refresh]);

  const handlePrevious = () => {
    if (pagination.offset > 0) {
      setPagination((prev) => ({
        ...prev,
        offset: Math.max(0, prev.offset - prev.limit),
      }));
    }
  };

  const handleNext = () => {
    if (pagination.offset + pagination.limit < pagination.total) {
      setPagination((prev) => ({
        ...prev,
        offset: prev.offset + prev.limit,
      }));
    }
  };
  return (
    <ErrorBoundary FallbackComponent={ErrorFallback}>
      <div className="space-y-3 lg:space-y-4">
        {/* Header Card */}
        <div className="bg-white rounded-lg lg:rounded-xl shadow-sm border border-gray-200 p-3 lg:p-4">
          <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3">
            <div className="flex-1">
              <h2 className="text-base lg:text-xl font-bold text-gray-900">
                API Usage Records
              </h2>
              <p className="text-xs lg:text-sm text-gray-500 mt-1 hidden sm:block">
                View and monitor Gemini API usage statistics
              </p>
            </div>
            <button
              className="bg-gradient-to-r from-blue-600 to-blue-700 text-white px-3 lg:px-6 py-2 lg:py-2.5 rounded-lg hover:from-blue-700 hover:to-blue-800 transition-all duration-200 shadow-md hover:shadow-lg font-medium disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-2 w-full sm:w-auto justify-center text-sm lg:text-base"
              onClick={refresh}
              disabled={loading}
            >
              <svg
                className={`w-4 h-4 ${loading ? "animate-spin" : ""}`}
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
                />
              </svg>
              <span>{loading ? "Refreshing..." : "Refresh"}</span>
            </button>
          </div>
        </div>

        {/* Date Search Filter Card */}
        <div className="bg-white rounded-lg lg:rounded-xl shadow-sm border border-gray-200 p-3 lg:p-4">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-3 lg:gap-4">
            {/* Search by Account/Driver */}
            <div>
              <label className="block text-xs lg:text-sm font-semibold text-gray-700 mb-2">
                Search by Account or Driver
              </label>
              <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2">
                <div className="relative flex-1">
                  <svg
                    className="absolute left-3 top-2.5 lg:top-3 w-4 h-4 lg:w-5 lg:h-5 text-gray-400 pointer-events-none"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                    />
                  </svg>
                  <input
                    type="text"
                    placeholder="Search by account ID, business name, email, driver name, license number..."
                    value={searchQuery}
                    onChange={handleSearchChange}
                    className="w-full pl-10 pr-3 lg:pr-4 py-2 lg:py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none transition-all duration-200 font-medium text-gray-700 text-sm lg:text-base"
                  />
                </div>
                {searchQuery && (
                  <button
                    onClick={clearSearch}
                    className="px-3 lg:px-4 py-2 lg:py-2.5 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition-all duration-200 font-medium text-sm lg:text-base whitespace-nowrap"
                  >
                    Clear
                  </button>
                )}
              </div>
              {searchQuery && (
                <p className="text-xs lg:text-sm text-blue-600 font-medium mt-2">
                  Found {filteredRecords.length} matching record
                  {filteredRecords.length !== 1 ? "s" : ""}
                </p>
              )}
            </div>

            {/* Date Filter */}
            <div>
              <label className="block text-xs lg:text-sm font-semibold text-gray-700 mb-2">
                Search by Date
              </label>
              <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2">
                <input
                  type="date"
                  value={selectedDate}
                  onChange={handleDateChange}
                  className="px-3 lg:px-4 py-2 lg:py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none transition-all duration-200 font-medium text-gray-700 text-sm lg:text-base flex-1 sm:flex-none"
                />
                {selectedDate && (
                  <button
                    onClick={() => {
                      setSelectedDate("");
                      setPagination((prev) => ({ ...prev, offset: 0 }));
                    }}
                    className="px-3 lg:px-4 py-2 lg:py-2.5 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition-all duration-200 font-medium text-sm lg:text-base"
                  >
                    Clear
                  </button>
                )}
                {selectedDate && (
                  <span className="text-xs lg:text-sm text-gray-600 font-medium whitespace-nowrap">
                    <span className="text-blue-600 font-semibold">
                      {selectedDate}
                    </span>
                  </span>
                )}
              </div>
            </div>
          </div>
        </div>

        {error && (
          <div className="bg-red-50 border-l-4 border-red-500 text-red-700 px-3 lg:px-4 py-2 lg:py-3 rounded-lg shadow-sm text-sm">
            <div className="flex items-center">
              <svg
                className="w-4 h-4 mr-2 flex-shrink-0"
                fill="currentColor"
                viewBox="0 0 20 20"
              >
                <path
                  fillRule="evenodd"
                  d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z"
                  clipRule="evenodd"
                />
              </svg>
              <span className="font-medium">{error}</span>
            </div>
          </div>
        )}

        {loading ? (
          <div className="bg-white rounded-lg lg:rounded-xl shadow-sm border border-gray-200 p-8 lg:p-12">
            <div className="text-center">
              <div className="inline-block animate-spin rounded-full h-10 w-10 lg:h-12 lg:w-12 border-4 border-blue-600 border-t-transparent mb-3 lg:mb-4"></div>
              <p className="text-gray-600 font-medium text-sm lg:text-base">
                Loading AI usage records...
              </p>
            </div>
          </div>
        ) : (
          <>
            {/* Unified Data Table Block */}
            <div className="bg-white rounded-lg lg:rounded-xl shadow-sm border border-gray-200 overflow-hidden">
              {/* Header with Controls */}
              <div className="border-b border-gray-200 bg-gradient-to-r from-gray-50 to-gray-100 px-3 lg:px-6 py-3 lg:py-4">
                <div className="flex flex-col lg:flex-row justify-between items-start lg:items-center gap-3 lg:gap-4">
                  {/* Left: Rows Per Page Selector */}
                  <div className="flex items-center gap-2 lg:gap-3">
                    <label className="text-xs lg:text-sm font-semibold text-gray-700 whitespace-nowrap">
                      Display:
                    </label>
                    <select
                      value={
                        rowsPerPage === pagination.total ? "all" : rowsPerPage
                      }
                      onChange={handleRowsPerPageChange}
                      className="px-3 lg:px-4 py-2 lg:py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none transition-all duration-200 font-medium text-gray-700 text-xs lg:text-sm hover:border-gray-400 bg-white"
                    >
                      <option value="10">10 rows</option>
                      <option value="25">25 rows</option>
                      <option value="50">50 rows</option>
                      <option value="all">All rows</option>
                    </select>
                  </div>

                  {/* Right: Page Info & Stats */}
                  <div className="flex flex-col sm:flex-row items-start sm:items-center gap-3 lg:gap-4 w-full lg:w-auto">
                    {/* Token Counters */}
                    <div className="flex items-center gap-2 lg:gap-3">
                      <div className="bg-gradient-to-r from-blue-50 to-blue-100 px-3 py-2 rounded-lg border border-blue-200 text-xs lg:text-sm text-gray-700 shadow-sm">
                        <div className="text-[10px] text-blue-600 font-semibold uppercase tracking-wider">
                          Daily Total
                        </div>
                        <div className="text-base lg:text-lg font-bold text-blue-700 mt-0.5">
                          {dailyTotalTokens.toLocaleString()}
                        </div>
                        <div className="text-[9px] text-blue-500">tokens</div>
                      </div>
                      <div className="bg-gradient-to-r from-red-50 to-red-100 px-3 py-2 rounded-lg border border-red-200 text-xs lg:text-sm text-gray-700 shadow-sm">
                        <div className="text-[10px] text-red-600 font-semibold uppercase tracking-wider">
                          Max Per Minute
                        </div>
                        <div className="text-base lg:text-lg font-bold text-red-700 mt-0.5">
                          {maxPerMinuteTokens.toLocaleString()}
                        </div>
                        <div className="text-[9px] text-red-500">tokens</div>
                      </div>
                    </div>
                    {/* Page Navigation Info */}
                    <div className="text-xs lg:text-sm text-gray-600 font-medium bg-white px-3 py-1.5 rounded-lg border border-gray-200">
                      {rowsPerPage === pagination.total ? (
                        <span>
                          <span className="text-blue-600 font-semibold">
                            All {pagination.total}
                          </span>{" "}
                          records
                        </span>
                      ) : (
                        <span>
                          Page{" "}
                          <span className="text-blue-600 font-semibold">
                            {Math.floor(pagination.offset / rowsPerPage) + 1}
                          </span>{" "}
                          of{" "}
                          <span className="text-blue-600 font-semibold">
                            {Math.ceil(pagination.total / rowsPerPage) || 1}
                          </span>
                        </span>
                      )}
                    </div>

                    {/* Record Count */}
                    <div className="text-xs lg:text-sm text-gray-600 font-medium bg-blue-50 px-3 py-1.5 rounded-lg border border-blue-200 flex items-center gap-1 w-full sm:w-auto justify-center lg:justify-start">
                      <svg
                        className="w-4 h-4 text-blue-600"
                        fill="currentColor"
                        viewBox="0 0 20 20"
                      >
                        <path d="M10 12a2 2 0 100-4 2 2 0 000 4z" />
                        <path
                          fillRule="evenodd"
                          d="M.458 10C1.732 5.943 5.522 3 10 3s8.268 2.943 9.542 7c-1.274 4.057-5.064 7-9.542 7S1.732 14.057.458 10zM14 10a4 4 0 11-8 0 4 4 0 018 0z"
                          clipRule="evenodd"
                        />
                      </svg>
                      <span>
                        Showing{" "}
                        <span className="font-semibold text-blue-700">
                          {pagination.offset + 1}–
                          {Math.min(
                            pagination.offset + pagination.limit,
                            pagination.total,
                          )}
                        </span>{" "}
                        of{" "}
                        <span className="font-semibold text-gray-700">
                          {pagination.total}
                        </span>
                      </span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Table Content */}
              <div className="overflow-x-auto">
                <AiUsesTable
                  records={filteredRecords}
                  startIndex={pagination.offset}
                />
              </div>

              {/* Footer with Pagination */}
              {rowsPerPage !== pagination.total && (
                <div className="border-t border-gray-200 bg-gray-50 px-3 lg:px-6 py-3 lg:py-4">
                  <div className="flex flex-col sm:flex-row justify-between items-center gap-3 lg:gap-4">
                    <button
                      onClick={handlePrevious}
                      disabled={pagination.offset === 0 || loading}
                      className="px-4 lg:px-6 py-2 lg:py-2.5 bg-white border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:bg-white transition-all duration-200 font-medium flex items-center space-x-2 w-full sm:w-auto justify-center text-sm lg:text-base shadow-sm hover:shadow-md"
                    >
                      <svg
                        className="w-4 h-4"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M15 19l-7-7 7-7"
                        />
                      </svg>
                      <span>Previous</span>
                    </button>

                    <span className="text-xs lg:text-sm text-gray-600 font-medium bg-white px-4 py-1.5 rounded-lg border border-gray-200">
                      Page{" "}
                      <span className="font-semibold text-gray-800">
                        {Math.floor(pagination.offset / rowsPerPage) + 1}
                      </span>{" "}
                      of{" "}
                      <span className="font-semibold text-gray-800">
                        {Math.ceil(pagination.total / rowsPerPage) || 1}
                      </span>
                    </span>

                    <button
                      onClick={handleNext}
                      disabled={
                        pagination.offset + rowsPerPage >= pagination.total ||
                        loading
                      }
                      className="px-4 lg:px-6 py-2 lg:py-2.5 bg-white border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:bg-white transition-all duration-200 font-medium flex items-center space-x-2 w-full sm:w-auto justify-center text-sm lg:text-base shadow-sm hover:shadow-md"
                    >
                      <span>Next</span>
                      <svg
                        className="w-4 h-4"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M9 5l7 7-7 7"
                        />
                      </svg>
                    </button>
                  </div>
                </div>
              )}
            </div>
          </>
        )}
      </div>
    </ErrorBoundary>
  );
}
